import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";

import { useNavigate } from "react-router-dom";

import {
  getHrLeaveBalances,
  deleteDuplicateBalances,
  getUsers,
  runMonthlyLeaveCredit,
} from "../../api/api";

import AdjustBalanceDialog from "./components/AdjustBalanceDialog";
import LeaveImport from "./LeaveImport";
import CycleActivityDialog from "./components/CycleActivityDialog";
import UserTransactionsDialog from "./components/UserTransactionsDialog";


/* =========================================================
   HELPERS
========================================================= */

const getInitials = (name) => {
  if (!name) return "?";

  const parts = name.trim().split(/\s+/);

  if (parts.length > 1) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  return parts[0].slice(0, 2).toUpperCase();
};

const avatarColor = (name) => {
  const colors = [
    "#1f4494",
    "#0f766e",
    "#9333ea",
    "#c2410c",
    "#0369a1",
    "#be123c",
  ];

  let hash = 0;

  for (let i = 0; i < (name || "").length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const balanceColor = (value) => {
  const number = Number(value || 0);

  if (number <= 0) return "error";
  if (number < 5) return "warning";

  return "success";
};

const formatDate = (date) => {
  if (!date) return "—";

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) return "—";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};


/* =========================================================
   SMALL COMPONENTS
========================================================= */

function SummaryCard({
  icon,
  label,
  value,
  subtitle,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: { xs: "100%", sm: 180 },
        borderRadius: 2,
        boxShadow: "none",
      }}
    >
      <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "action.hover",
            }}
          >
            {icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {label}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}


function BalanceChip({ label, value }) {
  return (
    <Chip
      size="small"
      label={`${label}: ${value ?? 0}`}
      color={balanceColor(value)}
      variant="outlined"
      sx={{
        fontWeight: 600,
        borderRadius: 1.5,
      }}
    />
  );
}


function LoadingRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <TableRow key={index}>
          <TableCell>
            <Skeleton width={70} />
          </TableCell>

          <TableCell>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton width={120} />
            </Stack>
          </TableCell>

          <TableCell>
            <Skeleton width={180} />
          </TableCell>

          <TableCell align="right">
            <Skeleton width={50} sx={{ ml: "auto" }} />
          </TableCell>

          <TableCell align="right">
            <Skeleton width={50} sx={{ ml: "auto" }} />
          </TableCell>

          <TableCell align="right">
            <Skeleton width={50} sx={{ ml: "auto" }} />
          </TableCell>

          <TableCell align="right">
            <Skeleton width={35} sx={{ ml: "auto" }} />
          </TableCell>

          <TableCell align="right">
            <Skeleton width={35} sx={{ ml: "auto" }} />
          </TableCell>

          <TableCell align="right">
            <Skeleton width={35} sx={{ ml: "auto" }} />
          </TableCell>

          <TableCell align="center">
            <Skeleton variant="circular" width={30} height={30} sx={{ mx: "auto" }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}


/* =========================================================
   MAIN PAGE
========================================================= */

export default function LeaveBalances() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [balances, setBalances] = useState([]);
  const [cycleRange, setCycleRange] = useState(null);

  // Active users used by the employee selection dropdown.
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [usersDropdownOpen, setUsersDropdownOpen] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [adjustUser, setAdjustUser] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const [activityUser, setActivityUser] = useState(null);
  const [activityTab, setActivityTab] = useState("applied");

  const [transactionDialogUser, setTransactionDialogUser] = useState(null);

  const [deleteDupOpen, setDeleteDupOpen] = useState(false);
  const [deletingDups, setDeletingDups] = useState(false);

  // ---- Monthly credit run state ----
  const [monthlyCreditDialogOpen, setMonthlyCreditDialogOpen] = useState(false);
  const [runningMonthlyCredit, setRunningMonthlyCredit] = useState(false);

  const searchTimerRef = useRef(null);
  const userSearchTimerRef = useRef(null);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);


  /* =========================================================
     LOAD DATA
  ========================================================= */

  const load = useCallback(
    async (
      pageNum,
      searchTerm,
      userIds = [],
      options = {}
    ) => {
      const requestId = ++requestIdRef.current;

      const isInitial = options.initial === true;

      if (isInitial) {
        setInitialLoading(true);
      } else {
        setTableLoading(true);
      }

      try {
        const data = await getHrLeaveBalances({
          page: pageNum,
          limit: pageSize,
          search: searchTerm,
          user_ids: userIds,
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        setBalances(
          Array.isArray(data?.balances)
            ? data.balances
            : []
        );

        setCycleRange({
          start: data?.cycle_start,
          end: data?.cycle_end,
        });

        setTotalCount(
          Number(data?.pagination?.total_count || 0)
        );

        setTotalPages(
          Number(data?.pagination?.total_pages || 0)
        );
      } catch (error) {
        console.error(
          "Failed to load leave balances:",
          error
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setInitialLoading(false);
          setTableLoading(false);
        }
      }
    },
    [pageSize]
  );


  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    load(1, "", [], { initial: true });
  }, [load]);


  /* =========================================================
     LOAD ACTIVE USERS (LAZY - only when dropdown opens)
  ========================================================= */

  useEffect(() => {
    if (!usersDropdownOpen || users.length > 0) {
      return;
    }

    let mounted = true;

    const loadActiveUsers = async () => {
      setUsersLoading(true);

      try {
        const response = await getUsers();

        const userList = Array.isArray(response)
          ? response
          : Array.isArray(response?.users)
            ? response.users
            : Array.isArray(response?.data)
              ? response.data
              : [];

        const validUsers = userList.filter(
          (user) =>
            user?.id !== undefined &&
            user?.id !== null
        );

        if (mounted) {
          setUsers(validUsers);
        }
      } catch (error) {
        console.error(
          "Failed to load active users:",
          error
        );

        if (mounted) {
          setUsers([]);
        }
      } finally {
        if (mounted) {
          setUsersLoading(false);
        }
      }
    };

    loadActiveUsers();

    return () => {
      mounted = false;
    };
  }, [usersDropdownOpen, users.length]);

  /* =========================================================
     USER SELECTION
  ========================================================= */

  const allUserIds = useMemo(
    () =>
      users
        .map((user) =>
          Number(user?.id ?? user?.user_id)
        )
        .filter((id) => Number.isFinite(id)),
    [users]
  );

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();

    if (!query) {
      return users;
    }

    return users.filter((user) => {
      const name = String(user?.name || "").toLowerCase();
      const email = String(user?.email || "").toLowerCase();
      const id = String(user?.id ?? user?.user_id ?? "");

      return (
        name.includes(query) ||
        email.includes(query) ||
        id.includes(query)
      );
    });
  }, [users, userSearch]);

  const allVisibleUserIds = useMemo(
    () =>
      filteredUsers
        .map((user) =>
          Number(user?.id ?? user?.user_id)
        )
        .filter((id) => Number.isFinite(id)),
    [filteredUsers]
  );

  const allUsersSelected =
    allUserIds.length > 0 &&
    allUserIds.every((id) => selectedUserIds.includes(id));

  const allVisibleUsersSelected =
    allVisibleUserIds.length > 0 &&
    allVisibleUserIds.every((id) =>
      selectedUserIds.includes(id)
    );

  const someVisibleUsersSelected =
    allVisibleUserIds.some((id) =>
      selectedUserIds.includes(id)
    ) && !allVisibleUsersSelected;

  const handleUserSelection = useCallback((event) => {
    const value = event.target.value;

    const ids =
      typeof value === "string"
        ? value
            .split(",")
            .map(Number)
            .filter((id) => Number.isFinite(id))
        : value
            .map(Number)
            .filter((id) => Number.isFinite(id));

    setSelectedUserIds(ids);
    setPage(1);
  }, []);

  const handleSelectAllUsers = useCallback(() => {
    if (allVisibleUsersSelected) {
      setSelectedUserIds((current) =>
        current.filter(
          (id) => !allVisibleUserIds.includes(id)
        )
      );
    } else {
      setSelectedUserIds((current) =>
        Array.from(
          new Set([
            ...current,
            ...allVisibleUserIds,
          ])
        )
      );
    }

    setPage(1);
  }, [allVisibleUserIds, allVisibleUsersSelected]);

  /* =========================================================
     SEARCH DEBOUNCE (Main balance search)
  ========================================================= */

  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      const value = search.trim();

      setDebouncedSearch(value);

      setPage(1);
    }, 350);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [search]);


  /* =========================================================
     SEARCH / PAGINATION / USER FILTER LOAD
  ========================================================= */

  useEffect(() => {
    if (initialLoading) return;

    load(
      page,
      debouncedSearch,
      selectedUserIds
    );
  }, [
    page,
    debouncedSearch,
    selectedUserIds,
    initialLoading,
    load,
  ]);


  /* =========================================================
     SEARCH
  ========================================================= */

  const handleSearchChange = useCallback((event) => {
    setSearch(event.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  }, []);


  /* =========================================================
     PAGINATION
  ========================================================= */

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);


  /* =========================================================
     ACTIVITY
  ========================================================= */

  const openActivity = useCallback((user, tab) => {
    setActivityUser(user);
    setActivityTab(tab);
  }, []);


  /* =========================================================
     EXPORT
  ========================================================= */

  const handleExport = useCallback(() => {
    const header = [
      "Emp Code",
      "Name",
      "Email",
      "Planned Balance",
      "Unplanned Balance",
      "Total Balance",
      "Applied",
      "Approved",
      "LOP",
    ];

    const rows = balances.map((balance) => [
      balance.emp_code,
      balance.name,
      balance.email,
      balance.planned_balance,
      balance.unplanned_balance,
      balance.total_balance,
      balance.applied_count,
      balance.approved_count,
      balance.lop,
    ]);

    const csvContent = [
      header,
      ...rows,
    ]
      .map((row) =>
        row
          .map(
            (cell) =>
              `"${String(cell ?? "").replace(/"/g, '""')}"`
          )
          .join(",")
      )
      .join("\n");

    const blob = new Blob(
      [csvContent],
      {
        type: "text/csv;charset=utf-8;",
      }
    );

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.download =
      `leave_balances_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }, [balances]);


  /* =========================================================
     DELETE DUPLICATES
  ========================================================= */

  const handleDeleteDuplicates = useCallback(async () => {
    setDeletingDups(true);

    try {
      const response =
        await deleteDuplicateBalances();

      alert(
        `${response.duplicates_deleted} duplicate records deleted`
      );

      setDeleteDupOpen(false);

      load(
        page,
        debouncedSearch,
        selectedUserIds
      );
    } catch (error) {
      alert(
        `Error deleting duplicates: ${
          error?.message || "Unknown error"
        }`
      );
    } finally {
      setDeletingDups(false);
    }
  }, [page, debouncedSearch, selectedUserIds, load]);


  /* =========================================================
     RUN MONTHLY LEAVE CREDIT

     - Opens a confirmation dialog explaining the business rule.
     - On confirm, calls the existing backend job trigger.
     - Backend owns all crediting logic; this is only a manual
       trigger + status/result surface.
  ========================================================= */

  const handleRunMonthlyCredit = useCallback(async () => {
    setRunningMonthlyCredit(true);

    try {
      const response = await runMonthlyLeaveCredit();

      alert(
        response?.message ||
          "Monthly leave credit run completed successfully."
      );

      setMonthlyCreditDialogOpen(false);

      load(
        page,
        debouncedSearch,
        selectedUserIds
      );
    } catch (error) {
      alert(
        `Error running monthly leave credit: ${
          error?.message || "Unknown error"
        }`
      );
    } finally {
      setRunningMonthlyCredit(false);
    }
  }, [page, debouncedSearch, selectedUserIds, load]);


  /* =========================================================
     SUMMARY
  ========================================================= */

  const totalPlanned = useMemo(
    () =>
      balances.reduce(
        (sum, item) =>
          sum + Number(item.planned_balance || 0),
        0
      ),
    [balances]
  );

  const totalUnplanned = useMemo(
    () =>
      balances.reduce(
        (sum, item) =>
          sum + Number(item.unplanned_balance || 0),
        0
      ),
    [balances]
  );

  const totalLop = useMemo(
    () =>
      balances.reduce(
        (sum, item) =>
          sum + Number(item.lop || 0),
        0
      ),
    [balances]
  );


  /* =========================================================
     INITIAL LOADING
  ========================================================= */

  if (initialLoading) {
    return (
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <Skeleton width={180} height={36} />

        <Skeleton
          width={280}
          height={24}
          sx={{ mb: 3 }}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          {[1, 2, 3, 4].map((item) => (
            <Skeleton
              key={item}
              variant="rounded"
              height={76}
              sx={{ flex: 1 }}
            />
          ))}
        </Stack>

        <Skeleton
          variant="rounded"
          height={58}
          sx={{ mb: 2 }}
        />

        <Paper variant="outlined">
          <Table>
            <TableBody>
              <LoadingRows />
            </TableBody>
          </Table>
        </Paper>
      </Box>
    );
  }


  /* =========================================================
     PAGE
  ========================================================= */

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: "#f7f8fa",
        p: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1600,
          mx: "auto",
        }}
      >

        {/* =====================================================
            HEADER
        ===================================================== */}

        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            lg: "center",
          }}
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.02em",
              }}
            >
              Leave Balances
            </Typography>

            {cycleRange?.start && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Current cycle{" "}
                <strong>
                  {formatDate(cycleRange.start)}
                </strong>
                {" → "}
                <strong>
                  {formatDate(cycleRange.end)}
                </strong>
              </Typography>
            )}
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={
                <HistoryOutlinedIcon />
              }
              onClick={() =>
                navigate("/hr/activity-logs")
              }
            >
              Activity Logs
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={
                <DeleteOutlineIcon />
              }
              onClick={() =>
                setDeleteDupOpen(true)
              }
            >
              Clean Duplicates
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={
                runningMonthlyCredit ? (
                  <CircularProgress
                    size={16}
                    color="inherit"
                  />
                ) : (
                  <AccountBalanceWalletOutlinedIcon />
                )
              }
              onClick={() =>
                setMonthlyCreditDialogOpen(true)
              }
              disabled={runningMonthlyCredit}
            >
              {runningMonthlyCredit
                ? "Processing..."
                : "Run Monthly Credit"}
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={
                <FileDownloadOutlinedIcon />
              }
              onClick={handleExport}
            >
              Export
            </Button>

            <Button
              size="small"
              variant="contained"
              startIcon={
                <UploadFileOutlinedIcon />
              }
              onClick={() =>
                setImportOpen(true)
              }
            >
              Import
            </Button>
          </Stack>
        </Stack>


        {/* =====================================================
            SUMMARY
        ===================================================== */}

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <SummaryCard
            icon={
              <PeopleOutlineIcon fontSize="small" />
            }
            label="Employees"
            value={totalCount}
            subtitle={
              debouncedSearch
                ? "Matching employees"
                : "Total employees"
            }
          />

          <SummaryCard
            icon={
              <AccountBalanceWalletOutlinedIcon fontSize="small" />
            }
            label="Planned"
            value={totalPlanned.toFixed(1)}
            subtitle="Current page"
          />

          <SummaryCard
            icon={
              <AccountBalanceWalletOutlinedIcon fontSize="small" />
            }
            label="Unplanned"
            value={totalUnplanned.toFixed(1)}
            subtitle="Current page"
          />

          <SummaryCard
            icon={
              <EventBusyOutlinedIcon fontSize="small" />
            }
            label="LOP"
            value={totalLop.toFixed(1)}
            subtitle="Current page"
          />
        </Stack>


        {/* =====================================================
            SEARCH / TOOLBAR
        ===================================================== */}

        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            mb: 2,
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            alignItems={{
              xs: "stretch",
              md: "center",
            }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700 }}
              >
                Employee balances
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Search by employee name, email or code
              </Typography>
            </Box>

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1}
              sx={{
                width: {
                  xs: "100%",
                  md: "auto",
                },
              }}
            >
              {/* ACTIVE USER SELECTION */}

              <FormControl
                size="small"
                sx={{
                  minWidth: {
                    xs: "100%",
                    sm: 280,
                  },
                }}
              >
                <InputLabel id="leave-balance-user-select-label">
                  Employees
                </InputLabel>

                <Select
                  labelId="leave-balance-user-select-label"
                  multiple
                  value={selectedUserIds}
                  onChange={handleUserSelection}
                  onOpen={() => setUsersDropdownOpen(true)}
                  onClose={() => setUsersDropdownOpen(false)}
                  input={
                    <OutlinedInput label="Employees" />
                  }
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return "All active users";
                    }

                    if (
                      allUserIds.length > 0 &&
                      selected.length === allUserIds.length
                    ) {
                      return "All active users";
                    }

                    return `${selected.length} selected`;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 380,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      bgcolor: "background.paper",
                    }}
                    onKeyDown={(event) => {
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <TextField
                      fullWidth
                      autoFocus
                      size="small"
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(event) =>
                        setUserSearch(event.target.value)
                      }
                      onKeyDown={(event) => {
                        event.stopPropagation();
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: userSearch ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setUserSearch("")}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Box>

                  <MenuItem
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleSelectAllUsers();
                    }}
                  >
                    <Checkbox
                      checked={userSearch
                        ? allVisibleUsersSelected
                        : allUsersSelected}
                      indeterminate={
                        userSearch
                          ? someVisibleUsersSelected
                          : selectedUserIds.length > 0 &&
                            !allUsersSelected
                      }
                    />

                    <ListItemText
                      primary={
                        userSearch
                          ? "Select All Matching Users"
                          : "Select All"
                      }
                    />
                  </MenuItem>

                  <Divider />

                  {usersLoading ? (
                    <MenuItem disabled>
                      <CircularProgress
                        size={18}
                        sx={{ mr: 1 }}
                      />
                      Loading users...
                    </MenuItem>
                  ) : users.length === 0 ? (
                    <MenuItem disabled>
                      No users found
                    </MenuItem>
                  ) : filteredUsers.length === 0 ? (
                    <MenuItem disabled>
                      No users match "{userSearch}"
                    </MenuItem>
                  ) : (
                    filteredUsers.map((user) => {
                      const userId = Number(
                        user?.id ?? user?.user_id
                      );

                      const displayName =
                        user?.name ||
                        user?.full_name ||
                        user?.username ||
                        user?.email ||
                        `User ${userId}`;

                      const secondaryText =
                        user?.emp_code ||
                        user?.employee_code ||
                        user?.email ||
                        "";

                      return (
                        <MenuItem
                          key={userId}
                          value={userId}
                        >
                          <Checkbox
                            checked={selectedUserIds.includes(
                              userId
                            )}
                          />

                          <ListItemText
                            primary={displayName}
                            secondary={secondaryText}
                          />
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>

              {/* SEARCH */}

              <TextField
                value={search}
                onChange={handleSearchChange}
                size="small"
                placeholder="Search employees..."
                autoComplete="off"
                sx={{
                  width: {
                    xs: "100%",
                    sm: 300,
                    md: 360,
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "background.paper",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        color="action"
                      />
                    </InputAdornment>
                  ),

                  endAdornment: (
                    <InputAdornment position="end">
                      {tableLoading ? (
                        <CircularProgress
                          size={18}
                          thickness={5}
                        />
                      ) : search ? (
                        <IconButton
                          size="small"
                          onClick={clearSearch}
                          edge="end"
                          aria-label="Clear search"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>

          {(debouncedSearch || selectedUserIds.length > 0) && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1 }}
            >
              {selectedUserIds.length > 0 && (
                <Chip
                  size="small"
                  label={`Employees: ${selectedUserIds.length} selected`}
                  onDelete={() => {
                    setSelectedUserIds([]);
                    setUserSearch("");
                    setPage(1);
                  }}
                  deleteIcon={
                    <ClearIcon fontSize="small" />
                  }
                />
              )}

              {debouncedSearch && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Chip
                    size="small"
                    label={`Search: "${debouncedSearch}"`}
                    onDelete={clearSearch}
                    deleteIcon={
                      <ClearIcon fontSize="small" />
                    }
                  />
                </Stack>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {totalCount} result
                {totalCount === 1 ? "" : "s"}
              </Typography>
            </Stack>
          )}
        </Paper>


        {/* =====================================================
            DESKTOP TABLE
        ===================================================== */}

        {!isMobile ? (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              boxShadow: "none",
            }}
          >
            {tableLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 5,
                  height: 3,
                  overflow: "hidden",
                  bgcolor: "action.hover",
                }}
              >
                <Box
                  sx={{
                    width: "35%",
                    height: "100%",
                    bgcolor: "primary.main",
                    animation:
                      "leaveLoading 1s infinite ease-in-out",
                    "@keyframes leaveLoading": {
                      "0%": {
                        transform: "translateX(-100%)",
                      },
                      "100%": {
                        transform: "translateX(400%)",
                      },
                    },
                  }}
                />
              </Box>
            )}

            <TableContainer
              sx={{
                maxHeight: "calc(100vh - 330px)",
                minHeight: 400,
              }}
            >
              <Table
                stickyHeader
                size="small"
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Employee
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Name
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Email
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Planned
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Unplanned
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Total
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Applied
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Approved
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      LOP
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "#f5f7fa",
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {tableLoading && balances.length === 0 ? (
                    <LoadingRows />
                  ) : balances.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        align="center"
                        sx={{ py: 8 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          No employees found
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Try a different name, email
                          or employee code, or change
                          the selected employees.
                        </Typography>

                        {search && (
                          <Button
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={clearSearch}
                          >
                            Clear search
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    balances.map((balance) => (
                      <TableRow
                        key={balance.user_id}
                        hover
                        sx={{
                          "&:last-child td": {
                            borderBottom: 0,
                          },
                        }}
                      >
                        {/* EMPLOYEE */}

                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              balance.emp_code || "—"
                            }
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>

                        {/* NAME */}

                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <Avatar
                              sx={{
                                width: 34,
                                height: 34,
                                fontSize: 12,
                                bgcolor:
                                  avatarColor(
                                    balance.name
                                  ),
                              }}
                            >
                              {getInitials(
                                balance.name
                              )}
                            </Avatar>

                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  whiteSpace:
                                    "nowrap",
                                }}
                              >
                                {balance.name ||
                                  "Unknown"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                ID:{" "}
                                {balance.user_id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* EMAIL */}

                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 220,
                              overflow: "hidden",
                              textOverflow:
                                "ellipsis",
                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {balance.email ||
                              "—"}
                          </Typography>
                        </TableCell>

                        {/* PLANNED */}

                        <TableCell align="right">
                          <BalanceChip
                            label="Planned"
                            value={
                              balance.planned_balance
                            }
                          />
                        </TableCell>

                        {/* UNPLANNED */}

                        <TableCell align="right">
                          <BalanceChip
                            label="Unplanned"
                            value={
                              balance.unplanned_balance
                            }
                          />
                        </TableCell>

                        {/* TOTAL */}

                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                            }}
                          >
                            {balance.total_balance}
                          </Typography>
                        </TableCell>

                        {/* APPLIED */}

                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              balance.applied_count ??
                              0
                            }
                            clickable
                            variant="outlined"
                            onClick={() =>
                              openActivity(
                                balance,
                                "applied"
                              )
                            }
                          />
                        </TableCell>

                        {/* APPROVED */}

                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              balance.approved_count ??
                              0
                            }
                            clickable
                            variant="outlined"
                            color="success"
                            onClick={() =>
                              openActivity(
                                balance,
                                "approved"
                              )
                            }
                          />
                        </TableCell>

                        {/* LOP */}

                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              balance.lop ?? 0
                            }
                            clickable
                            variant="outlined"
                            color={
                              balance.lop > 0
                                ? "error"
                                : "default"
                            }
                            onClick={() =>
                              openActivity(
                                balance,
                                "lop"
                              )
                            }
                          />
                        </TableCell>

                        {/* ACTION */}

                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            <Tooltip title="View transactions">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setTransactionDialogUser(
                                    balance
                                  )
                                }
                              >
                                <ViewWeekOutlinedIcon
                                  fontSize="small"
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Adjust balance">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setAdjustUser(
                                    balance
                                  )
                                }
                              >
                                <EditOutlinedIcon
                                  fontSize="small"
                                />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (

          /* ===================================================
             MOBILE
          =================================================== */

          <Stack spacing={1.25}>
            {balances.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 5,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{ fontWeight: 700 }}
                >
                  No employees found
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Try another search.
                </Typography>
              </Paper>
            ) : (
              balances.map((balance) => (
                <Card
                  key={balance.user_id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    position: "relative",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          bgcolor:
                            avatarColor(
                              balance.name
                            ),
                        }}
                      >
                        {getInitials(
                          balance.name
                        )}
                      </Avatar>

                      <Box
                        sx={{
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                          }}
                          noWrap
                        >
                          {balance.name ||
                            "Unknown"}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {balance.email ||
                            "—"}
                        </Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.5}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            setTransactionDialogUser(
                              balance
                            )
                          }
                        >
                          <ViewWeekOutlinedIcon
                            fontSize="small"
                          />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() =>
                            setAdjustUser(
                              balance
                            )
                          }
                        >
                          <EditOutlinedIcon
                            fontSize="small"
                          />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1.5 }}
                      alignItems="center"
                    >
                      <Chip
                        size="small"
                        label={
                          balance.emp_code ||
                          "No code"
                        }
                        variant="outlined"
                      />

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        ID: {balance.user_id}
                      </Typography>
                    </Stack>

                    <Divider
                      sx={{ my: 1.5 }}
                    />

                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      useFlexGap
                      gap={0.75}
                    >
                      <BalanceChip
                        label="Planned"
                        value={
                          balance.planned_balance
                        }
                      />

                      <BalanceChip
                        label="Unplanned"
                        value={
                          balance.unplanned_balance
                        }
                      />

                      <Chip
                        size="small"
                        label={`Total: ${
                          balance.total_balance
                        }`}
                        sx={{
                          fontWeight: 600,
                        }}
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        size="small"
                        label={`Applied: ${
                          balance.applied_count ??
                          0
                        }`}
                        clickable
                        onClick={() =>
                          openActivity(
                            balance,
                            "applied"
                          )
                        }
                      />

                      <Chip
                        size="small"
                        label={`Approved: ${
                          balance.approved_count ??
                          0
                        }`}
                        color="success"
                        variant="outlined"
                        clickable
                        onClick={() =>
                          openActivity(
                            balance,
                            "approved"
                          )
                        }
                      />

                      <Chip
                        size="small"
                        label={`LOP: ${
                          balance.lop ?? 0
                        }`}
                        color={
                          balance.lop > 0
                            ? "error"
                            : "default"
                        }
                        variant="outlined"
                        clickable
                        onClick={() =>
                          openActivity(
                            balance,
                            "lop"
                          )
                        }
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))
            )}
          </Stack>
        )}


        {/* =====================================================
            PAGINATION
        ===================================================== */}

        {totalPages > 1 && (
          <Stack
            alignItems="center"
            sx={{ mt: 2.5 }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={
                isMobile
                  ? "small"
                  : "medium"
              }
              showFirstButton={!isMobile}
              showLastButton={!isMobile}
            />
          </Stack>
        )}


        {/* =====================================================
            DIALOGS
        ===================================================== */}

        <LeaveImport
          open={importOpen}
          onClose={() =>
            setImportOpen(false)
          }
          onSuccess={() =>
            load(
              page,
              debouncedSearch,
              selectedUserIds
            )
          }
        />

        <AdjustBalanceDialog
          open={!!adjustUser}
          onClose={() =>
            setAdjustUser(null)
          }
          onSuccess={() =>
            load(
              page,
              debouncedSearch,
              selectedUserIds
            )
          }
          user={adjustUser}
        />

        <CycleActivityDialog
          open={!!activityUser}
          onClose={() =>
            setActivityUser(null)
          }
          user={activityUser}
          initialTab={activityTab}
        />

        <UserTransactionsDialog
          open={!!transactionDialogUser}
          onClose={() =>
            setTransactionDialogUser(null)
          }
          user={transactionDialogUser}
        />


        {/* =====================================================
            DELETE DUPLICATES
        ===================================================== */}

        {deleteDupOpen && (
          <Paper
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              bgcolor: "rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <Card
              sx={{
                width: "100%",
                maxWidth: 460,
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  Clean Duplicate Records?
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  This will scan all leave balances,
                  identify duplicates by employee code,
                  and keep only the most recently
                  updated record.
                </Typography>

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={1}
                  sx={{ mt: 3 }}
                >
                  <Button
                    onClick={() =>
                      setDeleteDupOpen(false)
                    }
                    disabled={deletingDups}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    color="warning"
                    onClick={
                      handleDeleteDuplicates
                    }
                    disabled={deletingDups}
                    startIcon={
                      deletingDups ? (
                        <CircularProgress
                          size={16}
                          color="inherit"
                        />
                      ) : (
                        <DeleteOutlineIcon />
                      )
                    }
                  >
                    {deletingDups
                      ? "Cleaning..."
                      : "Proceed"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Paper>
        )}


        {/* =====================================================
            RUN MONTHLY CREDIT - CONFIRMATION DIALOG
        ===================================================== */}

        {monthlyCreditDialogOpen && (
          <Paper
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              bgcolor: "rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <Card
              sx={{
                width: "100%",
                maxWidth: 480,
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  Run Monthly Leave Credit?
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  This will run the monthly leave
                  credit job for the current cycle.
                </Typography>

                <Stack
                  spacing={0.75}
                  sx={{ mt: 1.5 }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      color="action"
                      sx={{ mt: "2px" }}
                    />
                    <Typography variant="body2">
                      Only <strong>active users</strong>{" "}
                      will be processed.
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      color="action"
                      sx={{ mt: "2px" }}
                    />
                    <Typography variant="body2">
                      Each eligible user gets{" "}
                      <strong>+1 planned</strong> and{" "}
                      <strong>+0.5 unplanned</strong>{" "}
                      leave for the current cycle.
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      color="action"
                      sx={{ mt: "2px" }}
                    />
                    <Typography variant="body2">
                      Users already credited for the
                      current cycle will{" "}
                      <strong>not</strong> be credited
                      again.
                    </Typography>
                  </Stack>
                </Stack>

                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ mt: 2 }}
                >
                  This action is safe to run multiple
                  times — the backend automatically
                  skips users already credited for the
                  current cycle.
                </Alert>

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={1}
                  sx={{ mt: 3 }}
                >
                  <Button
                    onClick={() =>
                      setMonthlyCreditDialogOpen(false)
                    }
                    disabled={runningMonthlyCredit}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRunMonthlyCredit}
                    disabled={runningMonthlyCredit}
                    startIcon={
                      runningMonthlyCredit ? (
                        <CircularProgress
                          size={16}
                          color="inherit"
                        />
                      ) : (
                        <AccountBalanceWalletOutlinedIcon />
                      )
                    }
                  >
                    {runningMonthlyCredit
                      ? "Processing..."
                      : "Run Credit"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Paper>
        )}
      </Box>
    </Box>
  );
}